/**
 * Zod validation schemas for the Add Plane wizard
 *
 * Provides per-step validation with proper error messages
 * and type inference for form data.
 */

import { z } from 'zod'

// Client type enum
export const clientTypeSchema = z.enum([
  'individual',
  'corporate',
  'government',
  'non_profit',
])

// Step 1: Existing client selection schema
export const existingClientSchema = z.object({
  clientId: z.string().min(1, 'Please select a client'),
  contactId: z.string().optional(), // Optional for existing clients
})

// Step 1: New client schema
export const newClientSchema = z
  .object({
    client_type: clientTypeSchema,
    company_name: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email_primary: z.string().email('Invalid email').optional().or(z.literal('')),
    phone_primary: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.client_type === 'individual') {
        return !!data.first_name?.trim() && !!data.last_name?.trim()
      }
      return !!data.company_name?.trim()
    },
    {
      message: 'Name is required based on client type',
      path: ['company_name'], // Will show on company_name field
    }
  )

// Step 1: New contact schema (mandatory for new clients)
export const newContactSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email_primary: z.string().email('Invalid email address'),
  phone_mobile: z.string().optional(),
})

// Step 0: Aircraft schema (tail_number optional when using temp tail)
export const aircraftSchema = z.object({
  aircraft_type_id: z.string().min(1, 'Please select an aircraft type'),
  tail_number: z
    .string()
    .max(20, 'Tail number must be at most 20 characters')
    .regex(
      /^[A-Z0-9_-]*$/i,
      'Tail number can only contain letters, numbers, hyphens, and underscores'
    )
    .transform((val) => val.toUpperCase())
    .optional()
    .or(z.literal('')),
})

// Step 3: Transient contract schema
export const transientContractSchema = z.object({
  type: z.literal('transient'),
  check_in: z.string().min(1, 'Check-in date is required'),
  expected_check_out: z.string().min(1, 'Expected departure is required'),
  booking_type: z.enum(['guaranteed', 'flex']).default('flex'),
  rate_type: z.enum(['per_night', 'flat', 'per_sqft']).nullable().optional(),
  rate_amount: z.number().positive().nullable().optional(),
})

// Step 3: Long-term lease contract schema
export const leaseContractSchema = z.object({
  type: z.literal('long_term'),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().optional(),
  lease_type: z
    .enum(['long_term', 'month_to_month', 'seasonal'])
    .default('month_to_month'),
  monthly_rate: z.number().positive('Monthly rate is required'),
  rate_basis: z.enum(['flat', 'per_sqft']).nullable().optional(),
  security_deposit: z.number().positive().nullable().optional(),
  // Fuel fields
  fuel_type: z.enum(['jet_a', 'avgas']).nullable().optional(),
  fuel_commitment_gallons_monthly: z.number().int().positive().nullable().optional(),
  fuel_commitment_gallons_annual: z.number().int().positive().nullable().optional(),
  fuel_price_per_gallon: z.number().positive().nullable().optional(),
  fuel_price_type: z.enum(['fixed', 'opis_plus', 'retail_discount']).nullable().optional(),
  fuel_price_differential: z.number().nullable().optional(),
})

// Step 3: Combined contract schema (discriminated union)
export const contractSchema = z.discriminatedUnion('type', [
  transientContractSchema,
  leaseContractSchema,
])

// Step 4: Dropdown location schema
export const dropdownLocationSchema = z.object({
  method: z.literal('dropdown'),
  area_id: z.string().optional(),
  unit_id: z.string().optional(),
})

// Step 4: Placement location schema
export const placementLocationSchema = z.object({
  method: z.literal('placement'),
  position_x: z.number(),
  position_y: z.number(),
  position_z: z.number().default(0),
  rotation: z.number().default(0),
  detected_area_id: z.string().optional(),
  detected_unit_id: z.string().optional(),
})

// Step 4: Combined location schema
export const locationSchema = z.discriminatedUnion('method', [
  dropdownLocationSchema,
  placementLocationSchema,
])

// Full wizard submission schema
export const wizardSubmissionSchema = z.object({
  // Mode
  is_quick_place: z.boolean().default(false),

  // Client (optional in quick-place mode)
  client_id: z.string().optional(),
  new_client: newClientSchema.optional(),
  new_contact: newContactSchema.optional(),
  selected_contact_id: z.string().optional(),

  // Aircraft
  aircraft_type_id: z.string().min(1, 'Aircraft type is required'),
  tail_number: z.string().optional(), // Optional when using temp tail

  // Contract (optional in quick-place mode)
  contract: contractSchema.optional(),

  // Location
  location: locationSchema,
})

// Type exports for use in components
export type ClientType = z.infer<typeof clientTypeSchema>
export type ExistingClientFormData = z.infer<typeof existingClientSchema>
export type NewClientFormData = z.infer<typeof newClientSchema>
export type NewContactFormData = z.infer<typeof newContactSchema>
export type AircraftFormData = z.infer<typeof aircraftSchema>
export type TransientContractFormData = z.infer<typeof transientContractSchema>
export type LeaseContractFormData = z.infer<typeof leaseContractSchema>
export type ContractFormData = z.infer<typeof contractSchema>
export type DropdownLocationFormData = z.infer<typeof dropdownLocationSchema>
export type PlacementLocationFormData = z.infer<typeof placementLocationSchema>
export type LocationFormData = z.infer<typeof locationSchema>
export type WizardSubmissionFormData = z.infer<typeof wizardSubmissionSchema>

// Helper to validate individual steps
export const stepValidators = {
  client: {
    existing: existingClientSchema,
    new: z.object({
      client: newClientSchema,
      contact: newContactSchema,
    }),
  },
  aircraft: aircraftSchema,
  contract: contractSchema,
  location: locationSchema,
}
